// Manager Ops Dashboard (Priority 8) — top-level KPI cards + team table +
// score-distribution histogram + criteria heatmap + journey-stage counts +
// coaching queue + alert feed. Manager-only.
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { Loader2, AlertTriangle, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useAuth } from '../../contexts/AuthContext';
import { GovernanceAlertsSheet } from './GovernanceAlertsSheet';
import { OperationalSearchButton } from '../../components/search/OperationalSearchModal';
import { ComplianceTrendSparkline } from '../../components/manager/ComplianceTrendSparkline';
import { PracticeLeaderboardCard } from '../../components/manager/PracticeLeaderboardCard';
import { DateRangePills, formatDaysLabel } from '../../components/intelligence/AnalyticsFilters';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HEATMAP_TONE = (pct) => {
  // pct is 0-100 (pass rate). Falls back to older 0-5 `avg` scale for
  // pre-fix payloads by multiplying by 20 upstream. Buckets tuned so
  // ≥80% is green, 60-80% teal (acceptable), 40-60% amber (train), <40%
  // rose (urgent training gap).
  if (pct == null) return 'bg-slate-100 text-slate-400';
  if (pct >= 80) return 'bg-emerald-500 text-white';
  if (pct >= 60) return 'bg-teal-400 text-white';
  if (pct >= 40) return 'bg-amber-400 text-slate-900';
  return 'bg-rose-500 text-white';
};

// Short-code the criterion description for the column header. First
// letters of each word up to 3 chars — "Identified fitness goals and
// motivations" → "IFG". Falls back to the first 3 chars if the
// description is a single word.
const shortCode = (label) => {
  const words = String(label || '').split(/\s+/).filter(Boolean);
  if (words.length >= 2) return words.slice(0, 4).map((w) => w[0]).join('').toUpperCase();
  return (words[0] || '—').slice(0, 3).toUpperCase();
};

const ALERT_TONE = {
  high:   'bg-rose-50 border-rose-200 text-rose-800',
  medium: 'bg-amber-50 border-amber-200 text-amber-800',
  low:    'bg-slate-50 border-slate-200 text-slate-700',
};

const ManagerOpsDashboardPage = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  // Command Center drill-down: /manager/ops?focus=<agent name> highlights
  // + scrolls to that agent's row in the team table.
  const location = useLocation();
  const focusAgent = new URLSearchParams(location.search).get('focus') || '';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [domainId, setDomainId] = useState(user?.role === 'master_admin' ? '' : (user?.domain_id || ''));
  const [domains, setDomains] = useState([]);
  useEffect(() => {
    if (!focusAgent || !data) return;
    const el = document.getElementById('focused-agent-row');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [focusAgent, data]);

  const [govSheetOpen, setGovSheetOpen] = useState(false);
  // Date-range pill — null = All, preset number, or 'YYYY-MM-DD:YYYY-MM-DD'.
  const [days, setDays] = useState(30);

  useEffect(() => {
    if (user?.role !== 'master_admin' || !token) return;
    axios.get(`${API}/admin/sync-status`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setDomains(Array.isArray(r.data) ? r.data : []))
      .catch(() => setDomains([]));
  }, [token, user?.role]);

  useEffect(() => {
    if (!token) return;
    if (user?.role === 'master_admin' && !domainId) { setData(null); setLoading(false); return; }
    setLoading(true);
    const daysParam = days == null ? 'all' : String(days);
    const q = `?days=${encodeURIComponent(daysParam)}${domainId ? `&domain_id=${domainId}` : ''}`;
    axios.get(`${API}/manager/ops-dashboard${q}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [token, domainId, days, user?.role]);

  const distributionRows = useMemo(() => {
    if (!data?.score_distribution) return [];
    return Object.entries(data.score_distribution).map(([bucket, count]) => ({ bucket, count }));
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
      </div>
    );
  }
  if (user?.role === 'master_admin' && !domainId) {
    return (
      <div className="max-w-6xl mx-auto p-6 space-y-4">
        <h1 className="text-2xl font-bold">Manager Ops Dashboard</h1>
        <p className="text-slate-600 text-sm">Select a tenant to view the dashboard:</p>
        <select
          value={domainId}
          onChange={(e) => setDomainId(e.target.value)}
          className="border border-slate-300 rounded px-3 py-2 text-sm"
          data-testid="ops-domain-select"
        >
          <option value="">— Choose a tenant —</option>
          {domains.map((d) => <option key={d.domain_id || d.id} value={d.domain_id || d.id}>{d.company_name || d.name || d.domain_id || d.id}</option>)}
        </select>
      </div>
    );
  }
  if (!data) return <div className="p-6 text-slate-500">No data available.</div>;

  const cards = data.top_cards || {};
  const consultantLabel = data.consultant_label || 'Consultant';

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="manager-ops-dashboard">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Manager Ops Dashboard</h1>
          <p className="text-sm text-slate-500">Site overview · {formatDaysLabel(days)} · min {data.ranking_min_calls} calls to rank</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <ComplianceTrendSparkline days={30} />
          <OperationalSearchButton data-testid="manager-ops-operational-search" />
        </div>
      </header>

      {/* Date-range pills — same selector as Team Pulse / Sales Funnel */}
      <div className="flex items-center justify-between gap-4 flex-wrap bg-white border border-slate-200 rounded-xl px-4 py-2.5" data-testid="manager-ops-filters">
        <DateRangePills days={days} onChange={setDays} />
      </div>

      {/* Top cards */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Qualifying calls" value={cards.qualifying_calls} />
        <KpiCard label="Site median score" value={cards.site_median} tone="teal" />
        <KpiCard label="Appt. progression" value={`${cards.appointment_progression_rate ?? 0}%`} tone="sky" />
        <KpiCard label="Leads needing action" value={cards.leads_requiring_action} tone="amber" />
        <KpiCard label="Coaching overdue" value={cards.coaching_actions_overdue} tone={cards.coaching_actions_overdue > 0 ? 'rose' : 'slate'} />
        <KpiCard
          label="Governance alerts"
          value={cards.governance_alerts}
          tone={cards.governance_alerts > 0 ? 'rose' : 'slate'}
          onClick={cards.governance_alerts > 0 ? () => setGovSheetOpen(true) : undefined}
          testid="kpi-governance-alerts"
        />
        <KpiCard label="No-shows" value={cards.no_shows} tone="slate" />
      </section>

      {/* Alerts */}
      {data.alerts?.length > 0 && (
        <section className="space-y-2">
          {data.alerts.map((a, i) => {
            const clickable = a.kind === 'governance_violation';
            return (
              <div
                key={i}
                className={`border rounded px-3 py-2 flex items-center gap-2 ${ALERT_TONE[a.severity] || ALERT_TONE.low} ${
                  clickable ? 'cursor-pointer hover:brightness-95' : ''
                }`}
                onClick={clickable ? () => setGovSheetOpen(true) : undefined}
                data-testid={`alert-${a.kind}`}
              >
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm font-semibold">{a.message}</span>
                {clickable && (
                  <ArrowRight className="ml-auto w-4 h-4 opacity-70" />
                )}
              </div>
            );
          })}
        </section>
      )}

      {/* Score distribution + Journey stage */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-base">Score Distribution</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={distributionRows} margin={{ top: 5, right: 5, bottom: 5, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#14b8a6" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">Journey Stage Volume</CardTitle></CardHeader>
          <CardContent className="space-y-1">
            {Object.entries(data.journey_stage_board || {}).map(([stage, n]) => (
              <div key={stage} className="flex items-center justify-between text-sm py-1 border-b border-slate-100 last:border-0">
                <span className="text-slate-700 capitalize">{stage.replace(/_/g, ' ')}</span>
                <span className="font-semibold tabular-nums">{n}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      </section>

      {/* Team table */}
      <section>
        <Card>
          <CardHeader><CardTitle className="text-base">{data.team_label || 'Team'} — {consultantLabel} table</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                  <th className="py-2">{consultantLabel}</th>
                  <th className="py-2">Site</th>
                  <th className="py-2 text-right">Qualifying calls</th>
                  <th className="py-2 text-right">Avg / Median</th>
                  <th className="py-2 text-right">Range</th>
                  <th className="py-2 text-right">% ≥70</th>
                  <th className="py-2 text-right">Appts</th>
                  <th className="py-2 text-right">Progression</th>
                  <th className="py-2 text-right" title="Top filler words + interruption rate over transcribed calls">Filler top 3 · Int%</th>
                  <th className="py-2">Coaching focus</th>
                </tr>
              </thead>
              <tbody>
                {data.team_table.map((r) => {
                  const norm = (s) => String(s || '').split('·')[0].trim().toLowerCase();
                  const isFocus = focusAgent && norm(r.agent_name) === norm(focusAgent);
                  return (
                  <tr
                    key={r.agent_id}
                    id={isFocus ? 'focused-agent-row' : undefined}
                    className={`border-b border-slate-100 ${isFocus ? 'bg-amber-50 ring-2 ring-inset ring-amber-400' : ''}`}
                    data-testid={`team-row-${r.agent_id}`}
                  >
                    <td className="py-2 font-semibold text-slate-900">
                      <button
                        onClick={() => navigate(`/manager/agent/${encodeURIComponent(r.agent_id)}`)}
                        className="hover:text-teal-700 hover:underline text-left"
                        data-testid={`team-profile-link-${r.agent_id}`}
                        title="Open agent profile"
                      >
                        {r.agent_name}
                      </button>
                    </td>
                    <td className="py-2" data-testid={`team-site-${r.agent_id}`}>
                      {r.site ? (
                        <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">{r.site}</span>
                      ) : (
                        <span className="text-[11px] text-slate-300">—</span>
                      )}
                    </td>
                    <td className="py-2 text-right tabular-nums">
                      {r.qualifying_calls ?? 0}
                      {r.insufficient_sample && <span className="ml-1 text-[10px] uppercase text-amber-600">insuf.</span>}
                    </td>
                    <td className="py-2 text-right tabular-nums">
                      {r.avg_score ?? '—'} / {r.median_score ?? '—'}
                    </td>
                    <td className="py-2 text-right tabular-nums text-xs text-slate-500">
                      {r.score_range ? `${r.score_range[0]}–${r.score_range[1]}` : '—'}
                    </td>
                    <td className="py-2 text-right tabular-nums">{r.pct_above_70 ?? '—'}%</td>
                    <td className="py-2 text-right tabular-nums">{r.appointments_booked ?? 0}</td>
                    <td className="py-2 text-right tabular-nums">{r.appointment_progression_rate ?? 0}%</td>
                    <td className="py-2 text-right text-xs" data-testid={`team-lang-${r.agent_id}`}>
                      {r.language_patterns?.transcripts_analysed ? (
                        <div className="inline-flex flex-col items-end leading-tight">
                          <span className="text-slate-700">
                            {r.language_patterns.top_fillers.length === 0
                              ? <span className="text-emerald-600 italic">clean</span>
                              : r.language_patterns.top_fillers.map(f => (
                                  <span key={f.word} className="ml-1 font-mono bg-amber-50 text-amber-800 px-1 rounded text-[10px]">
                                    {f.word}·{f.uses}
                                  </span>
                                ))}
                          </span>
                          <span className={`text-[10px] mt-0.5 font-semibold ${
                            r.language_patterns.interruption_rate_pct < 5 ? 'text-emerald-700'
                              : r.language_patterns.interruption_rate_pct < 15 ? 'text-amber-700'
                                : 'text-rose-700'
                          }`}>
                            {r.language_patterns.interruption_rate_pct}% int · {r.language_patterns.transcripts_analysed}t
                          </span>
                        </div>
                      ) : (
                        <span className="text-slate-400 text-[10px]">—</span>
                      )}
                    </td>
                    <td className="py-2 text-xs">
                      {r.current_coaching_focus ? (
                        <span className={r.current_coaching_focus.overdue ? 'text-rose-600' : 'text-indigo-700'}>
                          {r.current_coaching_focus.focus_criterion}
                          {r.current_coaching_focus.overdue && <span className="ml-1 font-bold">·overdue</span>}
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </section>

      {/* Criteria heatmap */}
      <section>
        <Card>
          <CardHeader><CardTitle className="text-base">Criteria Heatmap — where the team needs training</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            {data.criteria_heatmap?.length ? (
              <>
                <table className="w-full text-xs">
                  <thead>
                    <tr>
                      <th className="text-left py-2 pr-2">{consultantLabel}</th>
                      {Object.entries(data.criteria_heatmap[0].scores).map(([desc, v]) => (
                        <th
                          key={desc}
                          className="py-2 px-1 text-center font-mono text-[10px] text-slate-500 min-w-[54px]"
                          title={`${desc}${v?.weight ? ` (weight ${v.weight})` : ''}`}
                        >
                          {shortCode(v?.label || desc)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.criteria_heatmap.map((row) => (
                      <tr key={row.agent_id}>
                        <td className="py-1 pr-2 font-semibold text-slate-800 whitespace-nowrap">
                          {row.agent_name}
                        </td>
                        {Object.entries(row.scores).map(([desc, v]) => (
                          <td key={desc} className="py-1 px-0.5">
                            <div
                              className={`text-center px-1 py-0.5 rounded text-[10px] font-bold ${HEATMAP_TONE(v.pass_pct)}`}
                              title={`${desc}: ${v.pass_pct == null ? 'no data' : `${v.pass_pct}% met`} over ${v.n} calls`}
                              data-testid={`heatmap-cell-${row.agent_id}-${desc.slice(0, 10)}`}
                            >
                              {v.pass_pct == null ? '—' : `${Math.round(v.pass_pct)}%`}
                            </div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-[11px] text-slate-500 mt-3 leading-snug">
                  Each cell is the % of graded calls where that criterion was met. Colours:{' '}
                  <span className="inline-block px-1.5 py-0.5 rounded bg-emerald-500 text-white font-mono">≥80</span>{' '}
                  <span className="inline-block px-1.5 py-0.5 rounded bg-teal-400 text-white font-mono">60–79</span>{' '}
                  <span className="inline-block px-1.5 py-0.5 rounded bg-amber-400 text-slate-900 font-mono">40–59</span>{' '}
                  <span className="inline-block px-1.5 py-0.5 rounded bg-rose-500 text-white font-mono">&lt;40</span>.
                  Hover a column header to see the full criterion name.
                </p>
              </>
            ) : (
              <p className="text-sm text-slate-500">No graded calls yet.</p>
            )}
          </CardContent>
        </Card>
      </section>

      {/* Coaching dashboard */}
      {data.coaching_dashboard?.length > 0 && (
        <section>
          <Card>
            <CardHeader><CardTitle className="text-base">Active Coaching Records</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {data.coaching_dashboard.map((r) => (
                <div key={r.id} className="flex items-center gap-3 border-b border-slate-100 py-2">
                  <div className="flex-1">
                    <p className="text-sm font-semibold">{r.focusCriterion}</p>
                    <p className="text-xs text-slate-500">
                      {r.consultantId} · baseline {r.baselineScore ?? '—'} ·
                      review {r.reviewDate ? new Date(r.reviewDate).toLocaleDateString() : '—'}
                    </p>
                  </div>
                  {r.overdue && <span className="text-xs text-rose-600 font-bold">OVERDUE</span>}
                  <span className={`text-xs px-2 py-0.5 rounded-full ${r.status === 'active' ? 'bg-indigo-100 text-indigo-800' : 'bg-slate-100 text-slate-700'}`}>{r.status}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </section>
      )}

      <PracticeLeaderboardCard domainId={domainId} />

      <GovernanceAlertsSheet
        open={govSheetOpen}
        onOpenChange={setGovSheetOpen}
        domainId={domainId || null}
        days={30}
      />
    </div>
  );
};

const KpiCard = ({ label, value, tone = 'slate', onClick = null, testid = null }) => {
  const tones = {
    slate:  'text-slate-800',
    teal:   'text-teal-700',
    sky:    'text-sky-700',
    amber:  'text-amber-700',
    rose:   'text-rose-700',
  };
  const clickable = typeof onClick === 'function';
  return (
    <div
      className={`bg-white border border-slate-200 rounded-lg p-3 ${
        clickable ? 'cursor-pointer hover:border-slate-400 hover:shadow-sm transition' : ''
      }`}
      onClick={clickable ? onClick : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      data-testid={testid || undefined}
    >
      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
        {label}
        {clickable && <span className="ml-1 text-slate-400">→</span>}
      </p>
      <p data-kpi-value className={`text-2xl font-bold tabular-nums ${tones[tone]}`}>{value ?? '—'}</p>
    </div>
  );
};

export default ManagerOpsDashboardPage;
